import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

const ProblemSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  // DESKTOP ONLY - Content rises from lower viewport
  const contentY = useTransform(scrollYProgress, [0, 0.4, 0.9], [150, 0, -80]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const glowOpacity = useTransform(scrollYProgress, [0, 0.5], [0.08, 0.15]);

  const statements = [
    'Most companies do not have an effort problem.',
    'They have a bottleneck problem.',
    'Growth slows when structure breaks.',
  ];

  const examples = [
    'A bad name can hurt trust.',
    'Weak positioning kills demand.',
    'The wrong market suppresses growth.',
    'Broken pricing creates friction.',
  ];

  return (
    <section ref={sectionRef} className="relative py-16 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-[#080808]" />

      {/* DESKTOP - Scroll-animated ambient glow */}
      <div className="hidden lg:block absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          style={{ opacity: glowOpacity }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] lg:w-[700px] lg:h-[700px] rounded-full bg-red-500 blur-[140px] lg:blur-[180px]"
        />
      </div>

      {/* MOBILE - Static ambient glow */}
      <div className="lg:hidden absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-red-500 blur-[140px] opacity-10" />
      </div>

      {/* DESKTOP - Scroll-animated content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="hidden lg:block relative z-10 max-w-[900px] mx-auto px-6 lg:px-8"
      >
        {/* Main statements */}
        <div className="mb-12 lg:mb-20">
          {statements.map((statement, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.8, delay: i * 0.15 }}
              className="text-[22px] leading-[1.5] lg:text-4xl font-bold text-white lg:leading-tight mb-5 lg:mb-8"
            >
              {statement}
            </motion.p>
          ))}
        </div>

        {/* Divider line */}
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="origin-left h-[2px] w-full max-w-[160px] lg:max-w-[200px] rounded-full mb-10 lg:mb-16"
          style={{ background: 'linear-gradient(90deg, #FF3B30, transparent)' }}
        />

        {/* Examples */}
        <div className="space-y-3 lg:space-y-5">
          {examples.map((example, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.6, delay: 0.6 + i * 0.1 }}
              className="text-[16px] leading-[1.7] lg:text-xl text-gray-400 lg:leading-relaxed"
            >
              {example}
            </motion.p>
          ))}
        </div>
      </motion.div>

      {/* MOBILE - Simple fade-in content, NO PARALLAX */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-50px' }}
        transition={{ duration: 0.6 }}
        className="lg:hidden relative z-10 max-w-[900px] mx-auto px-6"
      >
        {/* Main statements */}
        <div className="mb-10">
          {statements.map((statement, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="text-[24px] leading-[1.45] font-bold text-white mb-6"
            >
              {statement}
            </motion.p>
          ))}
        </div>

        {/* Divider line */}
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="origin-left h-[2px] w-full max-w-[160px] rounded-full mb-8"
          style={{ background: 'linear-gradient(90deg, #FF3B30, transparent)' }}
        />

        {/* Examples */}
        <div className="space-y-4">
          {examples.map((example, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.4, delay: 0.4 + i * 0.08 }}
              className="text-[17px] leading-[1.75] text-gray-400"
            >
              {example}
            </motion.p>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default ProblemSection;
